/**
 * Pure entity-builder functions for the OSM minimap's markers (issue #75;
 * the schematic canvas minimap these originally fed was removed in that
 * issue). Leaflet/canvas-free and world-local (metres, `bngToLocal`), so
 * they're testable in plain Node — `minimapMarkers.ts` converts their output
 * to WGS84 lon/lat for the Leaflet wrapper (`osmMinimap.ts`).
 */
import type { Location, TurbineScenario, Viewpoint } from '../core/manifest';
import { bngToLocal } from '../core/coordinates';

export function turbineEntities(scenario: TurbineScenario): Array<{ x: number; z: number; label: string }> {
  return scenario.turbines.map((t) => {
    const { x, z } = bngToLocal(t.easting, t.northing);
    return { x, z, label: t.id };
  });
}

export interface ViewpointEntity {
  /** The viewpoint's id, kept so a marker click can drive rig.jumpToViewpoint
   * via the same path as the viewpoint dropdown (issue #75). */
  id: string;
  x: number;
  z: number;
  label: string;
}

/** Resolved viewpoints only — a viewpoint with a pending (null) coordinate
 * is never given a fabricated position (hard constraint #7); it stays
 * reachable only via the dropdown's greyed-out entry. */
export function viewpointEntities(viewpoints: Viewpoint[]): ViewpointEntity[] {
  return viewpoints
    .filter((v): v is Viewpoint & { easting: number; northing: number } => v.easting !== null && v.northing !== null)
    .map((v) => {
      const { x, z } = bngToLocal(v.easting, v.northing);
      return { id: v.id, x, z, label: v.name };
    });
}

export function locationEntities(locations: Location[]): Array<{ x: number; z: number; label: string }> {
  return locations.map((l) => {
    const { x, z } = bngToLocal(l.easting, l.northing);
    return { x, z, label: l.name };
  });
}
